import React, { useEffect, useMemo, useState } from "react"
import Client from "../../models/client"
import { useForm } from "@mantine/form"
import { createEnduserEvent, updateEnduserEvent } from "../api"
import { notifications } from "@mantine/notifications"
import { BaseModal, requiredMark, requireLabelStyles } from "./BaseModal"
import { ActionIcon, Box, Button, Divider, Group, NumberInput, ScrollArea, Select, Stack, Text, TextInput } from "@mantine/core"
import { MinusIcon, PlusIcon } from "@phosphor-icons/react"
import { confirmDiscardChanges } from "../utils/confirm"

export type EnduserEvent = {
  id: number
  name: string
  record_type: string
  options?: { cv_point?: number; additional_cv_points?: number[] }
}

export const recordTypeOptions = [
  { value: 'web_cv', label: 'WebCV' },
  { value: 'coming_cv', label: '来店CV' },
  { value: 'coming_coupon_cv', label: '来店CVクーポン' },
]

export const recordTypeLabel = (value: string) =>
  recordTypeOptions.find((o) => o.value === value)?.label ?? value

interface Props {
  opened: boolean
  onClose: () => void
  event: EnduserEvent | null
  client: Client
  onSaved: () => void
}

type PointValue = number | string

const EnduserEventForm: React.FC<Props> = ({
  opened,
  onClose,
  event,
  client,
  onSaved,
}) => {
  const [submitting, setSubmitting] = useState(false)

  const selectableOptions = useMemo(() => {
    const serviceUse = String(client.invy_service_use)
    const options = recordTypeOptions.filter((o) => {
      if (o.value === 'web_cv') return serviceUse === '0' || serviceUse === '2'
      return serviceUse === '1' || serviceUse === '2'
    })

    const current = event ? recordTypeOptions.find((o) => o.value === event.record_type) : undefined
    if (current && !options.some((o) => o.value === current.value)) return [...options, current]
    return options
  }, [client.invy_service_use, event])

  const form = useForm<{
    name: string
    record_type: string
    cvPoints: PointValue[]
  }>({
    initialValues: {
      name: '',
      record_type: selectableOptions[0]?.value ?? 'web_cv',
      cvPoints: [''],
    },
    validate: {
      name: (v) => (v.trim() ? null : 'イベント名は必須です'),
    },
  })

  useEffect(() => {
    if (!opened) return

    if (event) {
      const main = event.options?.cv_point
      const additional = event.options?.additional_cv_points ?? []
      const points: PointValue[] = [
        ...(main !== undefined && main !== null ? [main] : []),
        ...additional,
      ]
      form.setValues({
        name: event.name,
        record_type: event.record_type,
        cvPoints: points.length ? points : [''],
      })
    } else {
      form.setValues({
        name: '',
        record_type: selectableOptions[0]?.value ?? 'web_cv',
        cvPoints: [''],
      })
    }
    form.resetDirty()
  }, [opened, event])

  const handleClose = () => {
    if (
      form.isDirty() &&
      !confirmDiscardChanges()
    ) {
      return
    }
    onClose()
  }

  const handleSubmit = async (values: typeof form.values) => {
    const isWebCv = values.record_type === 'web_cv'

    // メインCVポイント（1行目）は必須
    const mainRaw = String(values.cvPoints[0] ?? '').trim()
    if (isWebCv && !/^[0-9]+$/.test(mainRaw)) {
      form.setFieldError('cvPoints', 'メインCVポイントを入力してください')
      return
    }

    const options: EnduserEvent['options'] = {}
    if (isWebCv) {
      options.cv_point = Number(mainRaw)
      const additional = values.cvPoints
        .slice(1)
        .map((v) => String(v).trim())
        .filter((v) => /^[0-9]+$/.test(v))
        .map((v) => Number(v))
      if (additional.length > 0) {
        options.additional_cv_points = additional
      }
    }

    setSubmitting(true)
    try {
      if (event) {
        await updateEnduserEvent(
          client.id,
          event.id,
          values.name,
          values.record_type,
          options,
        )
        notifications.show({ message: 'イベントを更新しました', color: 'teal' })
      } else {
        await createEnduserEvent(
          client.id,
          values.name,
          values.record_type,
          options,
        )
        notifications.show({ message: 'イベントを作成しました', color: 'teal' })
      }
      onSaved()
      onClose()
    } catch (e) {
      console.error(
        event ? 'イベントの更新に失敗しました' : 'イベントの作成に失敗しました',
        e,
      )
      notifications.show({
        message: event
          ? 'イベントの更新に失敗しました'
          : 'イベントの作成に失敗しました',
        color: 'red',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const isWebCv = form.values.record_type === 'web_cv'

  return (
    <BaseModal
      opened={opened}
      onClose={handleClose}
      title={event ? 'イベント編集' : 'イベント新規作成'}
      width={560}
    >
      <form
        onSubmit={form.onSubmit(handleSubmit)}
        style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
      >
        <ScrollArea style={{ flex: 1, minHeight: 0 }} bg="#00000008" px={24}>
          <Stack gap="md" p="md">
            <Select
              label="レコードタイプ"
              data={selectableOptions}
              allowDeselect={false}
              {...form.getInputProps('record_type')}
            />

            <TextInput
              label={<>イベント名{requiredMark}</>}
              styles={requireLabelStyles}
              {...form.getInputProps('name')}
            />

            {isWebCv && <Divider />}

            {isWebCv && (
              <Box>
                <Group gap={8} align="baseline" mb={10}>
                  <Text fw={600} fz={16} c="#000000CC">CVポイント設定</Text>
                  <Text fz={13} c="#00000099">（複数の場合は「かつ」条件）</Text>
                </Group>
                <Stack gap={8}>
                  {form.values.cvPoints.map((_, index) => (
                    <Group key={index} gap={8} wrap="nowrap" align="flex-end">
                      <NumberInput
                        style={{ flex: 1 }}
                        label={index === 0 ? <>メインCVポイント{requiredMark}</> : `追加条件 ${index}`}
                        styles={index === 0 ? requireLabelStyles : undefined}
                        placeholder="数値のみ"
                        allowDecimal={false}
                        allowNegative={false}
                        hideControls
                        {...form.getInputProps(`cvPoints.${index}`)}
                      />
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        disabled={form.values.cvPoints.length === 1}
                        onClick={() => form.removeListItem('cvPoints', index)}
                      >
                        <MinusIcon size={20} />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>

                {form.errors.cvPoints && (
                  <Text c="red" fz={13} mt={6}>
                    {form.errors.cvPoints}
                  </Text>
                )}
                <Button
                  variant="subtle"
                  size="compact-sm"
                  mt={10}
                  leftSection={<PlusIcon size={16} />}
                  onClick={() => form.insertListItem('cvPoints', '')}
                >
                  条件を追加
                </Button>
              </Box>
            )}
          </Stack>
        </ScrollArea>

        <Box
          p="md"
          style={{
            borderTop: '1px solid #0000001A',
            backgroundColor: '#fff',
            flexShrink: 0,
          }}
        >
          <Group justify="flex-end" gap={8}>
            <Button
              variant="outline"
              radius="xl"
              color="gray"
              onClick={handleClose}
              disabled={submitting}
            >
              キャンセル
            </Button>
            <Button
              variant="filled"
              radius="xl"
              type="submit"
              loading={submitting}
            >
              保存
            </Button>
          </Group>
        </Box>
      </form>
    </BaseModal>
  )
}

export default EnduserEventForm
